import { useLingui } from "@lingui/react/macro";
import { cn } from "@rakazo/ui-web";
import { LoaderCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { DICTATION_MAX_MS, DICTATION_WARN_MS } from "../../lib/dictation";

/** Bars in the level meter; the newest level enters on the right. */
export const DICTATION_METER_BARS = 14;
const METER_TICK_MS = 100;
const CLOCK_TICK_MS = 500;

/** m:ss for a duration in whole seconds. */
export function formatDictationTime(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, "0")}`;
}

function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => clearInterval(timer);
  }, [active]);
  return now;
}

function useLevels(read: (() => number | null) | undefined, active: boolean): number[] | null {
  const [levels, setLevels] = useState<number[] | null>(null);
  useEffect(() => {
    if (!active || !read) {
      setLevels(null);
      return;
    }
    const timer = setInterval(() => {
      const level = read();
      setLevels((current) => {
        if (level === null) return null;
        const next = current ?? new Array<number>(DICTATION_METER_BARS).fill(0);
        return [...next.slice(1), level];
      });
    }, METER_TICK_MS);
    return () => clearInterval(timer);
  }, [read, active]);
  return levels;
}

/**
 * Takes the text field's place while dictating: a live dot, the time, the input level and,
 * near the five-minute limit, how long is left. While the server transcribes it shows a
 * spinner instead.
 */
export function DictationStrip({
  status,
  startedAt,
  transcript,
  readLevel,
}: {
  status: "listening" | "transcribing";
  startedAt: number;
  /** Words the browser already recognized live; the server path has none until the end. */
  transcript: string;
  readLevel?: () => number | null;
}) {
  const { t } = useLingui();
  const listening = status === "listening";
  const now = useNow(listening);
  const levels = useLevels(readLevel, listening);
  if (!listening) {
    return (
      <div
        data-testid="dictation-strip"
        className="flex min-h-6 min-w-0 flex-1 items-center gap-2.5 py-0.5 text-sm"
      >
        <LoaderCircle
          size={16}
          aria-hidden="true"
          className="shrink-0 animate-spin text-muted-foreground motion-reduce:animate-none"
        />
        <span role="status" className="truncate text-muted-foreground">
          {t`Transcribing…`}
        </span>
      </div>
    );
  }
  const elapsedMs = Math.min(DICTATION_MAX_MS, Math.max(0, now - startedAt));
  const seconds = Math.floor(elapsedMs / 1000);
  const left = Math.ceil((DICTATION_MAX_MS - elapsedMs) / 1000);
  const warn = elapsedMs >= DICTATION_WARN_MS;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return (
    <div
      data-testid="dictation-strip"
      className="flex min-h-6 min-w-0 flex-1 items-center gap-2.5 py-0.5 text-sm"
    >
      <span
        aria-hidden="true"
        className="size-2 shrink-0 animate-pulse rounded-full bg-destructive motion-reduce:animate-none"
      />
      <span
        role="timer"
        aria-label={t`Recording, ${minutes} min ${rest} s`}
        className="shrink-0 font-medium tabular-nums text-foreground/80"
      >
        {formatDictationTime(seconds)}
      </span>
      {warn ? (
        <span
          data-testid="dictation-limit"
          className="shrink-0 text-xs font-semibold text-destructive"
        >
          {t`${left} s left`}
        </span>
      ) : null}
      {transcript ? (
        <span className="min-w-0 truncate text-foreground" dir="auto" aria-live="polite">
          {transcript}
        </span>
      ) : levels && !warn ? (
        <span
          data-testid="dictation-meter"
          aria-hidden="true"
          className="flex h-[18px] min-w-0 flex-1 items-center gap-[2px] overflow-hidden"
        >
          {levels.map((level, index) => (
            <i
              key={index}
              className={cn(
                "block w-[3px] shrink-0 rounded-full",
                level > 0.08 ? "bg-destructive/80" : "bg-foreground/15",
              )}
              style={{ height: `${Math.round(4 + level * 14)}px` }}
            />
          ))}
        </span>
      ) : (
        <span className="truncate text-muted-foreground">{t`Listening…`}</span>
      )}
    </div>
  );
}
